import { useState, type FormEvent } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { Copy, Plus, Tablet } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/ui/Dialog';
import { toast } from '@/stores/toastStore';
import { bornesApi, type BornePointage } from '@/api/pointage';

/**
 * Pointage P2 — gestion des bornes (docs/PLAN-POINTAGE.md).
 * Le lien d'une borne n'est montré qu'une fois : il s'ouvre sur l'appareil de la borne et s'y attache.
 */
const dateHeure = (iso: string | null) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export const BornesPointage = ({ peutSaisir }: { peutSaisir: boolean }) => {
  const qc = useQueryClient();
  const bornes = useQuery({ queryKey: ['hr', 'pointage', 'bornes'], queryFn: bornesApi.lister });
  const [libelle, setLibelle] = useState('');
  const [lien, setLien] = useState<{ libelle: string; url: string } | null>(null);

  const creer = useMutation({
    mutationFn: () => bornesApi.creer(libelle.trim()),
    onSuccess: (b) => { setLien(b); setLibelle(''); void qc.invalidateQueries({ queryKey: ['hr', 'pointage', 'bornes'] }); },
  });
  const revoquer = useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => bornesApi.revoquer(id, motif),
    onSuccess: () => { toast.success('Borne désactivée'); void qc.invalidateQueries({ queryKey: ['hr', 'pointage', 'bornes'] }); },
  });
  const demanderRevocation = async (b: BornePointage) => {
    const motif = await demander.texte({ titre: `Désactiver « ${b.libelle} » ?`, libelle: 'Motif (5 caractères au moins)', min: 5, confirmer: 'Désactiver', danger: true });
    if (motif === null) return;
    revoquer.mutate({ id: b.id, motif });
  };
  const soumettre = (e: FormEvent) => { e.preventDefault(); if (libelle.trim().length >= 2) creer.mutate(); };
  const copier = async () => {
    if (!lien) return;
    try { await navigator.clipboard.writeText(lien.url); toast.success('Lien copié'); } catch { toast.warning('Copie impossible', 'Sélectionnez le lien à la main.'); }
  };

  return (
    <div className="ds-table-panel">
      <div className="ds-table-header">
        <div>
          <div className="ds-table-title"><Tablet size={14} style={{ verticalAlign: -2 }} /> Bornes de pointage</div>
          <div className="ds-table-sub">
            Un téléphone ou une tablette réservé, à l'entrée. Son lien ne sert que sur l'appareil où il est ouvert en premier.
          </div>
        </div>
      </div>
      <div style={{ padding: 16 }}>
        {peutSaisir && (
          <form onSubmit={soumettre} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <input className="ds-fi" style={{ flex: '1 1 220px' }} placeholder="Nom de la borne (ex. Entrée usine Bouaké)"
              value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={80} required />
            <button className="ds-btn ds-btn-p ds-btn-sm" type="submit" disabled={creer.isPending}>
              <Plus size={14} /> Créer une borne
            </button>
          </form>
        )}
        {bornes.isLoading ? 'Chargement…' : (bornes.data?.length ?? 0) === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune borne.</p>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="ds-tbl" style={{ minWidth: 560 }}>
              <thead>
                <tr><th>Borne</th><th style={{ width: 120 }}>État</th><th style={{ width: 150 }}>Dernier usage</th><th style={{ width: 90, textAlign: 'right' }}>Pointages</th><th style={{ width: 110 }} /></tr>
              </thead>
              <tbody>
                {bornes.data!.map((b) => (
                  <tr key={b.id}>
                    <td><div style={{ fontWeight: 600 }}>{b.libelle}</div><div style={{ fontSize: 11, color: 'var(--tx-3)' }}>créée le {dateHeure(b.createdAt)}</div></td>
                    <td>
                      {!b.actif ? <span className="ds-bd ds-bd-n" title={b.motifRevocation ?? ''}>Désactivée</span>
                        : b.appareilAttache ? <span className="ds-bd ds-bd-s">En service</span>
                        : <span className="ds-bd ds-bd-w">Lien pas encore ouvert</span>}
                    </td>
                    <td>{dateHeure(b.dernierUsage)}</td>
                    <td style={{ textAlign: 'right' }}>{b.pointages}</td>
                    <td>{peutSaisir && b.actif && (
                      <button className="ds-btn ds-btn-g ds-btn-sm" disabled={revoquer.isPending} onClick={() => demanderRevocation(b)}>Désactiver</button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!lien} onClose={() => setLien(null)} title={`Lien de la borne « ${lien?.libelle ?? ''} »`}
        subtitle="Ce lien ne sera plus affiché : ouvrez-le maintenant sur l'appareil de la borne (envoi par WhatsApp à vous-même, par exemple).">
        <div style={{ display: 'grid', gap: 12 }}>
          <textarea readOnly className="ds-fi" style={{ minHeight: 80, fontFamily: 'monospace', fontSize: 12 }} value={lien?.url ?? ''}
            onFocus={(e) => e.currentTarget.select()} />
          <ol style={{ fontSize: 13, paddingLeft: 18, margin: 0, display: 'grid', gap: 4 }}>
            <li>Ouvrez le lien dans Chrome sur l'appareil de la borne.</li>
            <li>Autorisez la caméra quand le téléphone le demande.</li>
            <li>Menu de Chrome › « Ajouter à l'écran d'accueil », puis ouvrez la borne depuis cette icône.</li>
            <li>Laissez l'appareil branché, écran allumé.</li>
          </ol>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="ds-btn ds-btn-g" onClick={() => void copier()}><Copy size={14} /> Copier</button>
            <button className="ds-btn ds-btn-p" onClick={() => setLien(null)}>J'ai copié le lien</button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
